import { useState } from 'react';
import Button from 'components/gruenland/atoms/button/Button';
import Checkbox, { CheckboxProps } from 'components/gruenland/atoms/checkbox';
import Input from 'components/gruenland/atoms/input';
import PasswordInput from 'components/gruenland/atoms/input-password';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { Account } from 'types/entity/account';
import Feedback from '../../account/account-atoms/feedback';
import { TYPOGRAPHY_MODULE_TITLE } from 'lib/typography';

interface Props {
  login?: (email: string, password: string, rememberMe?: boolean) => Promise<Account>;
  requestConfirmationEmail?: (email: string, password: string) => Promise<{ error?: boolean; message?: string }>;
  requestPasswordReset?: (email: string) => Promise<{ error?: boolean; message?: string }>;
}

const LoginForm: React.FC<Props> = ({ login, requestConfirmationEmail, requestPasswordReset }) => {
  //i18n messages
  const { formatMessage } = useFormat({ name: 'gruenland' });

  //login data
  const [data, setData] = useState({ email: '', password: '', rememberMe: false });

  //error
  const [error, setError] = useState('');

  //success
  const [success, setSuccess] = useState('');

  //processing...
  const [loading, setLoading] = useState(false);

  //attempting to resend verification email
  const [resendVerification, setResendVerification] = useState(false);

  //attempting to request a password reset
  const [resendPasswordReset, setResendPasswordReset] = useState(false);

  //not on default login modal
  const subModal = resendVerification || resendPasswordReset;

  const resetFeedback = () => {
    setError('');
    setSuccess('');
  };

  //get back to login modal
  const backToLogin = () => {
    setResendPasswordReset(false);
    setResendVerification(false);
    resetFeedback();
  };

  //requesting a password reset
  const toResendPassword = () => {
    setResendPasswordReset(true);
    setResendVerification(false);
    resetFeedback();
  };

  //handle text input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  //handle checkbox input change
  const handleCheckboxChange: CheckboxProps['onChange'] = ({ name, checked }) => {
    setData({ ...data, [name]: checked });
  };

  //login user
  const loginUser = async () => {
    try {
      const response = await login?.(data.email, data.password, data.rememberMe);

      if (!response?.accountId)
        setError(formatMessage({ id: 'error.wrongAuth', defaultMessage: 'Email o password errati.' }));
    } catch (err) {
      setError(
        formatMessage({ id: 'feedback.wentWrong', defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.' }),
      );
    }
  };

  //resend verification email for user
  const resendVerificationEmailForUser = async () => {
    try {
      const response = await requestConfirmationEmail?.(data.email, data.password);

      if (response?.error)
        setError(
          formatMessage({
            id: 'feedback.wentWrong',
            defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.',
          }),
        );
      else {
        setSuccess(
          formatMessage({
            id: 'feedback.emailSentTo',
            defaultMessage: 'Email inviata con successo a {email}.',
            values: { email: data.email },
          }),
        );
      }
    } catch (err) {
      setError(
        formatMessage({
          id: 'feedback.wentWrong',
          defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.',
        }),
      );
    }
  };

  //request a password reset for user
  const resendPasswordResetForUser = async () => {
    try {
      setError('');
      setSuccess('');
      const response = await requestPasswordReset?.(data.email);

      if (response?.error)
        setError(
          formatMessage({
            id: 'feedback.wentWrong',
            defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.',
          }),
        );
      else {
        setSuccess(
          formatMessage({
            id: 'feedback.emailSentTo',
            defaultMessage: 'Email inviata con successo a {email}.',
            values: { email: data.email },
          }),
        );
      }
    } catch (err) {
      setError(
        formatMessage({
          id: 'feedback.wentWrong',
          defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.',
        }),
      );
    }
  };

  //form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    //processing starts
    setLoading(true);
    //if user is attempting to resend verification email
    if (resendVerification) resendVerificationEmailForUser();
    //if user is attempting tor equest a password reset
    else if (resendPasswordReset) resendPasswordResetForUser();
    //if user wants to login
    else loginUser();
    //processing ends
    setLoading(false);
  };
  return (
    <>
      <Typography as="h3" className={TYPOGRAPHY_MODULE_TITLE}>
        {resendPasswordReset
          ? formatMessage({ id: 'passwordreset.title', defaultMessage: 'Reset password' })
          : formatMessage({ id: 'login.title', defaultMessage: 'Login' })}
      </Typography>
      <form className="mt-10" onSubmit={handleSubmit}>
        <Input
          id="email"
          name="email"
          type="email"
          label="Email"
          autoComplete="email"
          required
          className="mb-4 md:mb-5"
          onChange={handleChange}
        />

        {!resendPasswordReset && (
          <PasswordInput
            required
            id="password"
            name="password"
            label="Password"
            autoComplete="current-password"
            className="mb-4 md:mb-5"
            onChange={handleChange}
          />
        )}

        {!subModal && (
          <div className="mb-[30px] flex items-center justify-between">
            <Checkbox
              id="remember-me"
              name="rememberMe"
              onChange={handleCheckboxChange}
              label={formatMessage({ id: 'common.rememberMe', defaultMessage: 'Ricordami' })}
            />

            <Typography
              className="cursor-pointer text-[12px] text-secondary-black hover:underline md:text-[14px]"
              onClick={toResendPassword}
            >
              {formatMessage({ id: 'common.forgotPassword', defaultMessage: 'Password dimenticata?' })}
            </Typography>
          </div>
        )}

        <Feedback success={success} error={error} />

        <Button size="full" type="submit" className="mb-4 md:mb-5" disabled={loading}>
          {resendPasswordReset
            ? formatMessage({ id: 'common.getResetLink', defaultMessage: 'Ottieni il link per il reset' })
            : formatMessage({ id: 'login.title', defaultMessage: 'Login' })}
        </Button>

        {resendPasswordReset && (
          <Link
            variant="menu-item"
            className="mx-auto block w-fit cursor-pointer text-[14px]"
            link=""
            onClick={backToLogin}
          >
            {formatMessage({ id: 'passwordreset.cta', defaultMessage: 'Torna al login' })}
          </Link>
        )}
      </form>
    </>
  );
};

export default LoginForm;
